// Modules
import axios, { AxiosResponse } from "axios";
import get from "lodash.get";
// Helpers
import { generateToken } from "./jwt";

interface RequestAction {
    baseURL?: string;
    data?: any;
    method: "get" | "post" | "put" | "delete";
    params?: Record<string, any>;
    path: string;
    headers?: Record<string, string>;
}

interface ApiMethods {
    get: <T>(params: Omit<RequestAction, "method" | "data">) => Promise<AxiosResponse<T>>;
    post: <T>(params: Omit<RequestAction, "method">) => Promise<AxiosResponse<T>>;
    postFile: <T>(params: Omit<RequestAction, "method" | "params" | "headers">) => Promise<AxiosResponse<T>>;
    put: <T>(params: Omit<RequestAction, "method">) => Promise<AxiosResponse<T>>;
    putFile: <T>(params: Omit<RequestAction, "method" | "params" | "headers">) => Promise<AxiosResponse<T>>;
    delete: <T>(params: Omit<RequestAction, "method">) => Promise<AxiosResponse<T>>;
    url: () => string;
}

async function performRequest(action: RequestAction): Promise<AxiosResponse> {
    const { baseURL, data, method, params, path } = action;

    const token = generateToken();

    const options = {
        mode: "cors" as const,
        headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            ...get(action, "headers", {})
        }
    };

    return await axios({
        method,
        baseURL: baseURL || api.url(),
        url: path,
        headers: options.headers,
        params: params ? params : {},
        data: data
    });
}

const api: ApiMethods = {
    get: ({ path, params, baseURL }) => {
        return performRequest({ method: "get", path, params, baseURL });
    },
    post: ({ path, data, params, baseURL }) => {
        return performRequest({ method: "post", path, data, params, baseURL });
    },
    postFile: ({ path, data, baseURL }) => {
        return performRequest({
            method: "post",
            path,
            data,
            headers: {
                "Content-Type": "multipart/form-data"
            },
            baseURL
        });
    },
    put: ({ path, data, params, baseURL }) => {
        return performRequest({ method: "put", path, data, params, baseURL });
    },
    putFile: ({ path, data, baseURL }) => {
        return performRequest({
            method: "put",
            path,
            data,
            headers: {
                "Content-Type": "multipart/form-data"
            },
            baseURL
        });
    },
    delete: ({ path, data, params, baseURL }) => {
        return performRequest({ method: "delete", path, data, params, baseURL });
    },
    url: () => {
        return process.env.NEXT_PUBLIC_API_URL as string;
    }
};

export default api;
